import {of as observableOf, Observable, BehaviorSubject} from 'rxjs';

import {map, takeWhile} from 'rxjs/operators';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {Filter, FilterbuilderComponent} from './filterbuilder.component';
import {DisplayAttribute} from '../../resource/displayattribute';
import {LanguageService} from '../../language/language.service';
import {CacheService} from '../../cache/cache.service';
import {ControlParentInterface} from '../../resource/detail/control-parent.interface';
import {AddViewEvent} from '../../resource/events/addviewevent';
import {DetailControl, ValuesObject} from '../../resource/detail/detail-tabconfig';

@Component({
  selector: 'filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilterComponent implements ControlParentInterface, OnDestroy, AfterViewInit {
  @Input()
  set filter(filter: Filter) {
    this._filter = filter;
    if (this.datatypeSwitchString(filter.attribute) === 'Reference' && filter.search != null) {
      let val = this.referenceValueSubject.getValue();
      val.Values = filter.search;
      this.referenceValueSubject.next(val);
    }
  }

  get filter(): Filter {
    return this._filter;
  }

  private _filter: Filter;

  private referenceValueSubject: BehaviorSubject<ValuesObject<string>>;

  idPickerConfig: DetailControl = null;

  private htmlElement: HTMLElement;

  constructor(
    private cacheService: CacheService,
    elementRef: ElementRef,
  ) {
    const ipc = new DetailControl();
    let props: Observable<ValuesObject<any>>[] = [];
    let propsValues: Observable<any>[] = [];
    props['AutocompleteFilter'] = observableOf(new ValuesObject('/*'));
    props['UsageKeywords'] = observableOf(new ValuesObject('Global'));
    props['AttributesToSearch'] = observableOf(new ValuesObject('DisplayName,ObjectType'));
    this.referenceValueSubject = new BehaviorSubject<ValuesObject<string>>(
      new ValuesObject(null, null, 'object', 'ResourceID'),
    );
    props['Value'] = this.referenceValueSubject.asObservable();
    Object.keys(props).forEach(key => {
      propsValues[key] = props[key].pipe(
        map((valObj: ValuesObject<any>) => {
          if (valObj != null) {
            return valObj ? valObj.Values : null;
          } else {
            return null;
          }
        }),
      );
    });
    ipc.setProperties(props);
    ipc.setPropertyValues(propsValues);
    ipc.setEnabled(observableOf(new ValuesObject(true)));
    ipc.setFinalEnabled(observableOf(true));
    ipc.setExpandArea(observableOf(new ValuesObject(false)));
    ipc.setVisible(observableOf(new ValuesObject(true)));
    ipc.setHint(observableOf(new ValuesObject(null)));
    ipc.setRightsLevel(observableOf(['Read', 'Modify', 'Add', 'Remove']));
    ipc.setName(observableOf(new ValuesObject('FilterIDPicker')));
    ipc.setTypeName(observableOf(new ValuesObject('FilterIDPicker')));
    ipc.setDescription(observableOf(new ValuesObject('FilterIDPicker')));
    ipc.setCaption(observableOf(new ValuesObject('FilterIDPicker')));

    this.idPickerConfig = ipc;

    this.htmlElement = elementRef.nativeElement;

    this.onAddView.pipe(takeWhile(() => this._alive)).subscribe((e: AddViewEvent) => {
      window.open('#/' + e.siteType + '/' + e.urlid);
    });
  }

  private _alive: boolean = true;

  ngOnDestroy(): void {
    this._alive = false;
  }

  ngAfterViewInit(): void {
    this.refreshStyling();
  }

  @HostListener('window:resize', ['$event.target'])
  refreshStyling(): void {
    if (this.filter.attribute.DataType === 'Reference') {
      if (this.htmlElement.offsetWidth < 500) {
        this.htmlElement.style.flexWrap = 'wrap';
      } else {
        this.htmlElement.style.flexWrap = 'nowrap';
      }
    }
  }

  keywordLabel(attribute: DisplayAttribute, keyword: string): Observable<string> {
    return this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'FilterbuilderKeyword_' +
            (attribute.Multivalued ? 'MV_' : 'SV_') +
            attribute.DataType +
            '_' +
            keyword,
          'FilterbuilderKeyword_' +
            (attribute.Multivalued ? 'MV_' : 'SV_') +
            attribute.DataType +
            '_' +
            keyword,
          lr,
        );
      }),
    );
  }

  datatypeSwitchString(attribute: DisplayAttribute): string {
    if (['String', 'Text', 'ObjectType'].indexOf(attribute.DataType) !== -1) {
      return 'String';
    } else {
      return attribute.DataType;
    }
  }

  keywordsFor(attribute: DisplayAttribute) {
    return FilterbuilderComponent.keywordsFor(attribute);
  }

  get negateTooltip(): Observable<string> {
    return this.cacheService
      .getLanguageResource()
      .pipe(map(lr => LanguageService.translate('FilterbuilderNegate', 'Negate Filter', lr)));
  }

  // Control Parent

  @Output() onAddView: EventEmitter<AddViewEvent> = new EventEmitter<AddViewEvent>();

  updateResource(name: string, value: any): void {
    if (name === 'ResourceID') {
      this._filter.search = value;
    }
  }

  updateLocalizedValue(name: string, value: any): void {}

  updateLocale(value: any): void {}

  addControlError(control: string, error: string): void {}

  updateValidity(value: any): void {}
}
